import _ from "lodash"
import { Subject } from "rxjs"
import { distinctUntilChanged } from "rxjs/operators"
import { Bike } from "./Bike"
import { Mechanic } from "./Mechanic"

// constants
const BIKE_KEY = "garage.bike"
const DEFAULT_CONFIRMER = async () => true
const SELECT_HOOD = "HOOD"
const SELECT_WHEEL = "WHEEL"
const SELECT_PANEL = "PANEL"
const SELECT_GROUPS = {
  HOOD: [
    "1.BRP_SWI_BODY_PANELS_ACCY",
    "1.BRP_SWI_HOOD_ACCY",
    "1.BRP_SWI_HOOD_TRIM_ACCY",
  ],
  PANEL: ["1.BRP_SWI_SIDE_PANELS_ACCY", "1.BRP_SWI_BODY_PANELS_ACCY"],
  WHEEL: ["1.BRP_SWI_WHEEL_ACCENT_ACCY", "1.BRP_SWI_DECAL_WHEEL_ACCY"],
}

export class BikeChanges {
  static ResetType = "RESET"
  static UpdateType = "UPDATE"

  constructor(type, bikeUpdate) {
    this._type = type
    this._bikeUpdate = bikeUpdate
  }

  get type() {
    return this._type
  }

  get bike() {
    return this._bikeUpdate.bike
  }

  get changes() {
    return this._bikeUpdate.changes
  }
}

export class BikeCatalog {
  static Accessories = "accessories"
  static Cosmetics = "cosmetics"

  constructor(type, categories, bikeModel, selectGroup, bikeEngine) {
    this._type = type
    this._categories = categories
      .filter((c) => c.visible)
      .reduce((acc, c) => {
        const bc = new BikeCategory(type, c, bikeModel, selectGroup, bikeEngine)
        if (bc.parts.length > 0) {
          acc.push(bc)
        }
        return acc
      }, [])
  }

  get type() {
    return this._type
  }

  get categories() {
    return this._categories
  }
}

export class BikeCategory {
  constructor(type, category, bikeModel, selectGroup, bikeEngine) {
    this._type = type
    this._category = category
    this._parts = category.parts.filter(
      (p) =>
        !p.package ||
        p.package.includes("ALL") ||
        p.package.includes(bikeModel.sku) ||
        p.package.includes(bikeEngine.sku)
    )
    if (selectGroup && SELECT_GROUPS[selectGroup]) {
      const groupSubgroupsSet = new Set(SELECT_GROUPS[selectGroup])
      this._parts = this._parts.filter((part) =>
        groupSubgroupsSet.has(part.subcategoryId)
      )
    }
  }

  get type() {
    return this._type
  }

  get cameraPosition() {
    return this._category.cameraPos
  }

  get id() {
    return this._category.id
  }

  get name() {
    return this._category.name
  }

  get image() {
    return this._category.image
  }

  get parts() {
    return this._parts
  }
}

/**
 * Class managing bike
 *
 * @class Garage
 * @classdesc
 *  Creates mechanics
 *  Stores bike
 *  Provides access to active bike
 */
export class Garage {
  /**
   * Create a Garage
   * @param catalogManager
   */
  constructor(catalogManager) {
    this._catalogManager = catalogManager
    this._bike = null
    this._bikeChanges$ = new Subject()
  }

  get accessories() {
    return new BikeCatalog(
      BikeCatalog.Accessories,
      this._catalogManager.accessories,
      this.bikeModel,
      undefined,
      this.bikeEngine
    )
  }

  get cosmetics() {
    return new BikeCatalog(
      BikeCatalog.Cosmetics,
      this._catalogManager.cosmetics,
      this.bikeModel,
      undefined,
      this.bikeEngine
    )
  }

  get bike() {
    return this._bike
  }

  get bikeModel() {
    // get intersection of all models and bike parts
    const parts = _.intersectionWith(
      this._bike.parts,
      this._catalogManager.models,
      (lhs, rhs) => _.isEqual(lhs.sku, rhs.sku)
    )
    const bikeModel = _.head(parts)

    // return bike
    // console.log(`[Garage] bikeModel: ${bikeModel}`);
    return bikeModel
  }

  get bikeEngine() {
    // get intersection of all engines and bike parts
    const parts = _.intersectionWith(
      this._bike.parts,
      this._catalogManager.engines,
      (lhs, rhs) => _.isEqual(lhs.sku, rhs.sku)
    )
    const bikeEngine = _.head(parts)

    // return bike
    // console.log(`[Garage] bikeEngine: ${bikeEngine}`);
    return bikeEngine
  }

  get bikeChanges$() {
    return this._bikeChanges$.pipe(distinctUntilChanged())
  }

  group(selectGroup) {
    return new BikeCatalog(
      BikeCatalog.Cosmetics,
      this._catalogManager.cosmetics,
      this.bikeModel,
      selectGroup,
      this.bikeEngine
    )
  }

  async initialize() {
    this._bike = new Bike()
  }

  async resetBike(modelSku, engineSku, confirmer = DEFAULT_CONFIRMER) {
    // create new bike
    const mechanic = this._createMechanic()

    // add new engine / model
    mechanic.addPart(this._catalogManager.findModel(modelSku))
    mechanic.addPart(this._catalogManager.findEngine(engineSku))

    // build update and apply changes if confirmed
    const bikeUpdate = mechanic.build()
    if (await confirmer(bikeUpdate)) {
      await this._applyChanges(
        new BikeChanges(BikeChanges.ResetType, bikeUpdate)
      )
    }
  }

  async resetBikeSkus(skus, confirmer = DEFAULT_CONFIRMER) {
    // create new bike
    const mechanic = this._createMechanic()

    // add all parts related to skus
    const { inventory } = this._catalogManager
    for (const sku of skus) {
      // lookup first matching part
      const part = inventory.find((part) => part.sku === sku)

      // add part if found
      if (part) {
        mechanic.addPart(part)
      }

      // or display warning
      else {
        console.warn(`Garage: unable to find part for SKU: ${sku}`)
      }
    }

    // build update and apply changes if confirmed
    const bikeUpdate = mechanic.build()
    if (await confirmer(bikeUpdate)) {
      await this._applyChanges(
        new BikeChanges(BikeChanges.ResetType, bikeUpdate)
      )
    }
  }

  async addBikeParts(parts, confirmer = DEFAULT_CONFIRMER) {
    // create mechanic
    const mechanic = this._createMechanic(this._bike)

    // add parts
    parts.forEach((part) => mechanic.addPart(part))

    // build update and apply changes if confirmed
    const bikeUpdate = mechanic.build()
    if (await confirmer(bikeUpdate)) {
      await this._applyChanges(
        new BikeChanges(BikeChanges.UpdateType, bikeUpdate)
      )
    }
  }

  async removeBikeParts(parts, confirmer = DEFAULT_CONFIRMER) {
    // create mechanic
    const mechanic = this._createMechanic(this._bike)

    // add parts
    parts.forEach((part) => mechanic.removePart(part))

    // build update and apply changes if confirmed
    const bikeUpdate = mechanic.build()
    if (await confirmer(bikeUpdate)) {
      await this._applyChanges(
        new BikeChanges(BikeChanges.UpdateType, bikeUpdate)
      )
    }
  }

  totalAccessoryPrice() {
    const nonAccessorySkus = ["AR", "STD", "SPORT", "602", "903"]
    const modelSku = this.bikeModel.sku
    const price =
      this.bike.parts.reduce((price, part) => {
        // ignore the bike model and engine
        if (nonAccessorySkus.some((sku) => sku == part.sku)) {
          return price
        }
        // avoid rounding errors
        const basePrice = parseInt(
          this._catalogManager.priceForPart(part.sku) * 100
        )
        const modelPrice = this._catalogManager.priceForPart(part.sku, modelSku)
        if (modelPrice != undefined) {
          price += parseInt(modelPrice * 100)
        } else {
          price += basePrice
        }
        return price
      }, 0) / 100
    console.log("[Garage] accessories price: ", price)
    return price
  }

  modelEnginePrice() {
    let price = this._catalogManager.priceForPart(this.bikeModel.sku, this.bikeEngine.sku)
    console.log(
      `[Garage] engine model sku ${this.bikeModel.sku}.${this.bikeEngine.sku} price: `,
      price
    )

    return price
  }

  getTotalPrice() {
    let total =
      this.totalAccessoryPrice() + this.modelEnginePrice()
    console.log("[Garage] total price", total)
    return total
  }

  /**
   * This calculates monthly payment with given interest and given number of months (terms)
   *
   * @param rate
   * @param terms
   * @param totalPrice
   * @returns {number}
   */
  monthlyPrice({ rate = 4.99, terms = 60 } = {}, totalPrice) {
    rate = rate / 100
    let monthly = 0
    let monthlyRate = rate / 12
    if (monthlyRate > 0) {
      monthly =
        totalPrice *
        (monthlyRate + monthlyRate / (Math.pow(1 + monthlyRate, terms) - 1))
    } else {
      monthly = totalPrice / terms
    }
    return monthly
  }

  _createMechanic(bike = new Bike()) {
    return new Mechanic(bike, this._catalogManager.inventory)
  }

  async _setBike(bike) {
    // set bike
    this._bike = bike
  }

  async _applyChanges(bikeChanges) {
    // store bike
    await this._setBike(bikeChanges.bike)

    // fire update
    this._bikeChanges$.next(bikeChanges)
  }
}



// WEBPACK FOOTER //
// ./src/managers/Garage/Garage.js